import { ChevronLeft, ChevronRight } from 'lucide-react'

interface Column<T> {
  key: string
  label: string
  render?: (item: T) => React.ReactNode
  className?: string
}

interface Props<T> {
  columns: Column<T>[]
  data: T[]
  total: number
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  onRowClick?: (item: T) => void
  selectedId?: number | null
  getRowId: (item: T) => number
  emptyMessage?: string
  loading?: boolean
}

export default function DataTable<T>({ columns, data, total, page, totalPages, onPageChange, onRowClick, selectedId, getRowId, emptyMessage = 'No data found', loading }: Props<T>) {
  if (loading) {
    return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-800"></div></div>
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {columns.map(col => (
                <th key={col.key} className={`text-left py-2.5 px-3 font-semibold text-slate-600 text-xs uppercase tracking-wider ${col.className || ''}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-center py-8 text-slate-500">{emptyMessage}</td></tr>
            ) : (
              data.map(item => {
                const id = getRowId(item)
                const isSelected = selectedId === id
                return (
                  <tr key={id}
                    onClick={() => onRowClick?.(item)}
                    className={`border-b border-slate-100 transition-colors ${onRowClick ? 'cursor-pointer hover:bg-red-50' : ''} ${isSelected ? 'bg-red-50 border-red-200' : ''}`}>
                    {columns.map(col => (
                      <td key={col.key} className={`py-2.5 px-3 ${col.className || ''}`}>
                        {col.render ? col.render(item) : String((item as any)[col.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-3 py-3 border-t border-slate-200">
          <span className="text-xs text-slate-500">Showing {((page - 1) * 25) + 1}–{Math.min(page * 25, total)} of {total}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm px-3 text-slate-600">Page {page} of {totalPages}</span>
            <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
